import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  Input,
  OnChanges,
  Signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { BehaviorSubject, map, Observable } from 'rxjs';

import { MicrophoneSettings } from '@speaksee/speaksee-angular-core';

import { GuestSessionService } from 'src/app/services/guest-session.service';
import { Message } from '../../../shared/models/app.model';
import { Microphone } from 'src/app/shared/models/microphone.model';
import { SpeechService } from 'src/app/services/speech.service';

import {
  ILocalSettings,
  IRemoteSettings,
  SettingsService,
  Themes,
} from 'src/app/services/settings.service';

@Component({
  selector: 'app-speech-bubble',
  templateUrl: './speech-bubble.component.html',
  styleUrls: ['./speech-bubble.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SpeechBubbleComponent implements OnChanges {
  @Input() message: Message;
  @Input() prevTimestamp: Date;
  public themes = Themes;
  public currentTheme$: Observable<Themes>;
  public microphone: Microphone = null;
  public microphones: Microphone[];
  public isNaN = isNaN;
  public displayTS = false;
  public localLanguageOut$: Observable<string>;
  public showMessageMetadata$: Observable<boolean>;
  public localSettings$: Observable<ILocalSettings>;
  public micColorBright: string = '#FFFF00';
  public textColor: string = '#000000';

  // Use BehaviorSubject for micSettings to enable reactive updates
  private micSettingsSubject = new BehaviorSubject<MicrophoneSettings | null>(null);
  public micSettings$ = this.micSettingsSubject.asObservable();

  private qrUserService = inject(GuestSessionService);
  private destroyRef = inject(DestroyRef);

  // Check if user is a guest (QR or shared-session) and translations are explicitly disabled
  public isQRUser = this.qrUserService.isGuestUser();
  public isMicrophonekitQR = this.isQRUser && !this.qrUserService.isSoundshapeQR();
  public translationsDisabled: Signal<boolean> = computed(() =>
    this.isQRUser && this.qrUserService.guestTranslationsEnabled() === false
  );

  public isDelayActive: Signal<boolean> = computed(() =>
    this.isQRUser && this.qrUserService.isSoundshapeQR() && this.qrUserService.effectiveBufferMs() > 0
  );


  private latestMics: Microphone[] = [];

  constructor(
    private settingsService: SettingsService,
    private speechService: SpeechService,
  ) {
    this.localLanguageOut$ = this.settingsService.localLanguageOut$;
    this.localSettings$ = this.settingsService.localSettings$
    this.currentTheme$ = this.settingsService.theme$;
    this.showMessageMetadata$ = this.settingsService.showMessageMetadata$;

    this.speechService.activeMics$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((mics: Microphone[]) => {
        this.latestMics = mics;
        this.updateMicInfo();
      });
  }

  ngOnChanges() {
    this.updateMicInfo();
  }

  private updateMicInfo(): void {
    if (!this.message || !this.latestMics.length) return;
    const mic = this.latestMics.find((_mic) => _mic.id === this.message.micId);
    this.microphone = mic;
    if (mic && mic.color) {
      this.micColorBright = this.getBrightMicColor(mic.color);
      this.textColor = this.getContrastTextColor(mic.color);
    }
    this.micSettings$ = this.settingsService
      .getRemoteSetSettings(-1, false)
      .pipe(
        map((settings: IRemoteSettings) =>
          settings.microphones.find(
            (micSettings: MicrophoneSettings) =>
              micSettings.mic_id === mic?.id
          )
        )
      );
  }

  checkRender(timestamp1: any, timestamp2: Date): boolean {
    const date1 = new Date(timestamp1);
    const hours1 = date1.getHours();
    const minutes1 = date1.getMinutes();
    const date2 = new Date(timestamp2);
    const hours2 = date2.getHours();
    const minutes2 = date2.getMinutes();

    if (timestamp2 === undefined) {
      return true;
    } else if (hours1 === hours2 && minutes1 === minutes2) {
      return false;
    } else {
      return true;
    }
  }

  formatTime(timestamp: any) {
    if (timestamp) {
      const date = new Date(timestamp);
      const hours = date.getHours();
      const minutes = date.getMinutes();

      let stringMinutes = String(minutes);

      if (minutes < 10) {
        stringMinutes = `0${minutes}`;
      }

      return `${hours}:${stringMinutes}`;
    }
  }

  getMessageAfterStyle(color) {
    return 'transparent #' + color + ' transparent transparent';
  }

  private getBrightMicColor(color: string): string {
    if (!color) return '#FFFF00';
    
    // Convert hex to RGB
    const hex = color.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    
    // Increase brightness and saturation for Teletekst theme
    const brightness = 1.5;
    const newR = Math.min(255, Math.floor(r * brightness));
    const newG = Math.min(255, Math.floor(g * brightness));
    const newB = Math.min(255, Math.floor(b * brightness));
    
    // Ensure minimum brightness for visibility on black background
    const minBrightness = 150;
    const adjustedR = Math.max(minBrightness, newR);
    const adjustedG = Math.max(minBrightness, newG);
    const adjustedB = Math.max(minBrightness, newB);
    
    // Convert back to hex
    const toHex = (n: number) => n.toString(16).padStart(2, '0');
    return `#${toHex(adjustedR)}${toHex(adjustedG)}${toHex(adjustedB)}`;
  }

  /**
   * Calculate relative luminance for a color using WCAG formula
   */
  private calculateLuminance(hex: string): number {
    const cleanHex = hex.replace('#', '');
    const r = parseInt(cleanHex.substring(0, 2), 16) / 255;
    const g = parseInt(cleanHex.substring(2, 4), 16) / 255;
    const b = parseInt(cleanHex.substring(4, 6), 16) / 255;

    const luminance = (c: number) => {
      return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    };

    return 0.2126 * luminance(r) + 0.7152 * luminance(g) + 0.0722 * luminance(b);
  }

  /**
   * Calculate appropriate text color based on WCAG contrast ratios
   * Compares contrast with both black and white text, returns the one with higher contrast
   */
  private getContrastTextColor(backgroundColor: string): string {
    if (!backgroundColor) return '#000000';

    const bgLuminance = this.calculateLuminance(backgroundColor);

    // WCAG contrast ratio formula: (L1 + 0.05) / (L2 + 0.05) where L1 > L2
    // White has luminance of 1, black has luminance of 0
    const contrastWithWhite = (1 + 0.05) / (bgLuminance + 0.05);
    const contrastWithBlack = (bgLuminance + 0.05) / (0 + 0.05);

    // Return the color that provides higher contrast
    return contrastWithWhite > contrastWithBlack ? '#FFFFFF' : '#000000';
  }
}
